import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Select, SearchableSelect, RNLLoader, Modal, Btn, Card, useToast, StatusReason, ReturnDestinationNote } from '../UI';
import { SortTh, useSortState, sortRows } from '../requisition/SortableHeader';
import { PinnedDetail, useScrollViewport } from '../requisition/PinnedDetail';
import { ActionIcon, EyeIcon } from '../requisition/ActionIcons';
import Pagination from '../requisition/Pagination';
import * as api from '../../api';
import { considerationOptionsFor, defaultConsiderationFor, isAwaitingCorrection, correctionTarget } from '../../utils';
import { fmtDate, calcItemAmount } from './mpoUtils';
import MPOStatusPill from './MPOStatusPill';
import MPODetailView from './MPODetailView';

// Material Purchase Orders > Cost Control Review and Management Approval.
//
// The same review the Enterprise Requisition queues run, on PO lines:
//   Cost Control  four workspaces — Load (review lines and act), Reviewed (view only), Pending
//                 Consideration (POs parked by a return, grouped by why), Rejected (view only). Per line:
//                 BOQ item / sub-component, CC qty / rate / amount / GL, accept or reject. A PO-wide GL with
//                 "Same GL for every line".
//   Management    the PO with Cost Control's summary and figures read-only, its own approved qty / rate /
//                 amount and approve / hold / reject per line, and Approve / Return / Hold / Reject.
// Line reviews are saved with whichever action is taken; actions run in the approval engine.

const GROUP_COLOR = {
  blue: '#2563eb', green: '#16a34a', amber: '#d97706', red: '#dc2626',
  purple: '#7c3aed', cyan: '#0891b2', indigo: '#4f46e5', gray: '#64748b',
};

const num = (v) => (v != null && v !== '' && !isNaN(v) ? parseFloat(v).toLocaleString('en-NG', { minimumFractionDigits: 2 }) : '—');
const money = (v) => (v != null && v !== '' && !isNaN(v) ? `₦${num(v)}` : '—');
const blank = (v) => v === '' || v === null || v === undefined;

// A figure out of the API carries the column's scale ("20.0000"); two decimals where that is exact.
const cleanFigure = (v) => {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n === Number(n.toFixed(2)) ? n.toFixed(2) : String(n);
};

const ccSelectStyle = {
  width: 150, maxWidth: 150, boxSizing: 'border-box', padding: '4px 6px',
  border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11.5, background: '#f8fafc',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const ccSelectDisabledStyle = { ...ccSelectStyle, background: '#e2e8f0', color: '#94a3b8', cursor: 'not-allowed' };

const isCostControlStep = (name) => /cost control/i.test(name || '');

// Whether a return is filed under a consideration status: only when Management sends the PO back to Cost
// Control (that is what Pending Consideration tracks). Cost Control's own returns carry none.
const offersConsideration = (fromStep, targets, value) => {
  if (isCostControlStep(fromStep)) return false;
  if (!value || value === 'requester') return false;
  return isCostControlStep((targets || []).find((t) => t.value === value)?.label);
};

const WORKSPACES = [
  { key: 'load', icon: '📥', label: 'WORKSPACE 1', title: 'Load Purchase Orders', color: '#0b318b',
    desc: 'Purchase orders awaiting Cost Control review. Review lines, adjust quantities and rates, and forward.' },
  { key: 'reviewed', icon: '✅', label: 'WORKSPACE 2', title: 'Reviewed Purchase Orders', color: '#16a34a',
    desc: 'All purchase orders reviewed by Cost Control and forwarded onward. View only.' },
  { key: 'pending', icon: '⏳', label: 'WORKSPACE 3', title: 'Pending Consideration', color: '#d97706',
    desc: 'Purchase orders Management sent back to Cost Control — grouped by consideration type.' },
  { key: 'rejected', icon: '⛔', label: 'WORKSPACE 4', title: 'Rejected Purchase Orders', color: '#dc2626',
    desc: 'Purchase orders rejected outright by Cost Control. View only.' },
];

const ACTION_META = {
  approve: { label: 'Approve', title: 'Approve Purchase Order', color: '#16a34a', verb: 'approve', commentRequired: false, placeholder: 'Optional comment...' },
  return: { label: 'Return', title: 'Return Purchase Order', color: '#f97316', verb: 'return', commentRequired: true, placeholder: 'Explain why this purchase order is being returned...' },
  reject: { label: 'Reject', title: 'Reject Purchase Order', color: '#ef4444', verb: 'reject', commentRequired: true, placeholder: 'Explain why this purchase order is being rejected...' },
  hold: { label: 'Hold', title: 'Put Purchase Order On Hold', color: '#eab308', verb: 'put on hold', commentRequired: true, placeholder: 'Explain why this purchase order is being put on hold...' },
  resume: { label: 'Resume', title: 'Lift Hold', color: '#16a34a', verb: 'take off hold', commentRequired: false, placeholder: 'Optional note...' },
};

const OUTCOME_STYLE = {
  approve: { color: '#16a34a', bg: '#dcfce7', ring: '#f0fdf4', gradient: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', shadow: 'rgba(22,163,74,0.3)', icon: <polyline points="20 6 9 17 4 12" /> },
  return: { color: '#f97316', bg: '#ffedd5', ring: '#fff7ed', gradient: 'linear-gradient(135deg, #f97316 0%, #c2410c 100%)', shadow: 'rgba(249,115,22,0.3)', icon: (<><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></>) },
  reject: { color: '#ef4444', bg: '#fee2e2', ring: '#fef2f2', gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', shadow: 'rgba(239,68,68,0.3)', icon: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>) },
  hold: { color: '#eab308', bg: '#fef9c3', ring: '#fefce8', gradient: 'linear-gradient(135deg, #eab308 0%, #a16207 100%)', shadow: 'rgba(234,179,8,0.3)', icon: (<><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></>) },
  resume: { color: '#16a34a', bg: '#dcfce7', ring: '#f0fdf4', gradient: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', shadow: 'rgba(22,163,74,0.3)', icon: <polygon points="5 3 19 12 5 21 5 3" /> },
};

const thS = (extra = {}) => ({ padding: '9px 10px', textAlign: 'center', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', ...extra });
const tdS = (extra = {}) => ({ padding: '8px 10px', textAlign: 'center', fontSize: 12, ...extra });
const fieldLabel = { fontWeight: 700, color: '#64748b', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 };
const fieldValue = { fontWeight: 700, color: '#0f172a', fontSize: 12.5, marginTop: 2 };

const pill = (bg, extra = {}) => ({
  background: bg, color: '#fff', border: `1.5px solid ${bg}`, padding: '5px 16px', borderRadius: 20,
  fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s', ...extra,
});

const markBtn = (on, color, fg = color) => ({
  width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: `1.5px solid ${color}`, borderRadius: 6, fontSize: 13, fontWeight: 700, lineHeight: 1,
  cursor: 'pointer', background: on ? color : 'transparent', color: on ? '#fff' : fg, transition: 'all 0.15s',
});

function Field({ label, children, span }) {
  return (
    <div style={span ? { gridColumn: `span ${span}` } : undefined}>
      <div style={fieldLabel}>{label}</div>
      <div style={fieldValue}>{children ?? '—'}</div>
    </div>
  );
}

// The PO lines exactly as procurement raised them, read-only, above the working grid.
function OrderedLines({ items }) {
  const cols = [
    ['#', 50], ['ITEM CODE', 130], ['DESCRIPTION', 220], ['NEW ITEM DESC', 180], ['SPECIFICATION', 180],
    ['WAREHOUSE', 120], ['REQUEST REF', 140], ['BOQ CODE', 120], ['REM. RQST QTY', 110], ['UNIT', 70],
    ['QTY', 90], ['LAST PUR.PR', 110], ['PRICE (₦)', 110], ['DISC %', 70], ['VAT %', 70],
    ['TOTAL (₦)', 120], ['VAT AMT (₦)', 110], ['REMARK', 160],
  ];
  const td = { padding: '7px 10px', textAlign: 'center', fontSize: 12, border: '1px solid #cbd5e1' };
  return (
    <div style={{ padding: '10px 0 14px' }}>
      <div style={{ padding: '0 16px 8px', fontSize: 11.5, fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Line Items as Ordered
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr>
              {cols.map(([h, w]) => (
                <th key={h} style={{ ...thS({ fontSize: 10.5, color: '#fff', background: '#0b318bff', border: '1px solid #cbd5e1' }), minWidth: w }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.item_id} style={{ background: i % 2 ? '#f8fafc' : '#fff' }}>
                <td style={{ ...td, color: '#64748b' }}>{it.line_number || i + 1}</td>
                <td style={td}>{it.material_code || '—'}</td>
                <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{it.material_description || '—'}</td>
                <td style={{ ...td, textAlign: 'left' }}>{it.new_item_description || '—'}</td>
                <td style={{ ...td, textAlign: 'left' }}>{it.specification || '—'}</td>
                <td style={td}>{it.warehouse || '—'}</td>
                <td style={td}>{it.request_ref || '—'}</td>
                <td style={td}>{it.boq_item_code || '—'}</td>
                <td style={td}>{it.rem_rqst_qty != null ? num(it.rem_rqst_qty) : '—'}</td>
                <td style={td}>{it.unit_of_measure || '—'}</td>
                <td style={{ ...td, fontWeight: 600 }}>{num(it.quantity_ordered)}</td>
                <td style={td}>{it.last_pur_pr != null ? num(it.last_pur_pr) : '—'}</td>
                <td style={td}>{num(it.unit_rate)}</td>
                <td style={td}>{it.disc != null ? `${it.disc}%` : '—'}</td>
                <td style={td}>{it.vat_percentage != null ? `${it.vat_percentage}%` : '—'}</td>
                <td style={{ ...td, fontWeight: 700 }}>{num(it.amount)}</td>
                <td style={td}>{it.vat_amount != null ? num(it.vat_amount) : '—'}</td>
                <td style={{ ...td, textAlign: 'left' }}>{it.remarks || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function MPOReviewQueue({ stage = 'cost_control', view: viewProp, onViewChange }) {
  const isCC = stage === 'cost_control';
  const isMgmt = !isCC;
  const toast = useToast();

  // Cost Control's workspace lives in the URL (/cost-control/load); Management has one list.
  const [viewState, setViewState] = useState(null);
  const view = isMgmt ? 'management' : (viewProp !== undefined ? viewProp : viewState);
  const setView = onViewChange || setViewState;
  const isPendingView = isCC && view === 'pending';
  // Cost Control edits in Load and in Pending Consideration (rows sitting with them); Management always.
  const ccEditing = isCC && (view === 'load' || view === 'pending');

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [seenOptions, setSeenOptions] = useState({ projects: {}, suppliers: {} });
  const [counts, setCounts] = useState({ load: null, reviewed: null, pending: null, rejected: null });
  const [groups, setGroups] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [expanded, setExpanded] = useState({});
  const [viewing, setViewing] = useState(null);
  const [opening, setOpening] = useState(false);

  // lineEdits[poId][itemId] = { cc_qty, cc_rate, cc_amount, cc_gl, cc_approved, cc_rejected, cc_reason, boq_item_id, boq_component_id }
  const [lineEdits, setLineEdits] = useState({});
  // apprEdits[poId][itemId] = { qty, rate, amount, approved, on_hold, rejected, reason }
  const [apprEdits, setApprEdits] = useState({});
  const [ccHeader, setCcHeader] = useState({});
  const [glAllLines, setGlAllLines] = useState({});
  const [figureErrors, setFigureErrors] = useState({});
  const [boqByProject, setBoqByProject] = useState({});

  const [reasonModal, setReasonModal] = useState(null); // { poId, itemId, mode: 'cc'|'appr', status }
  const [actionModal, setActionModal] = useState(null); // { poId, instanceId, type, reviewed }
  const [actionComment, setActionComment] = useState('');
  const [nextStepInfo, setNextStepInfo] = useState(null);
  const [nextApproverId, setNextApproverId] = useState('');
  const [returnTargets, setReturnTargets] = useState([]);
  const [returnTarget, setReturnTarget] = useState('');
  const [returnFromStep, setReturnFromStep] = useState('');
  const [considerationStatuses, setConsiderationStatuses] = useState([]);
  const [considerationStatusId, setConsiderationStatusId] = useState('');
  const [preparing, setPreparing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);

  const sortState = useSortState('submitted_date', 'desc');
  const viewport = useScrollViewport([loading, view]);

  useEffect(() => { setPage((p) => (p.skip === 0 ? p : { ...p, skip: 0 })); }, [view, search, filterProject, filterSupplier]);

  // Picking a destination pre-selects its heading; leaving Cost Control clears it.
  useEffect(() => {
    if (!offersConsideration(returnFromStep, returnTargets, returnTarget)) { setConsiderationStatusId(''); return; }
    const label = (returnTargets || []).find((t) => t.value === returnTarget)?.label;
    const suggested = defaultConsiderationFor(label, considerationStatuses)
      || (isCostControlStep(label) ? (considerationStatuses.find((c) => /returned by approval/i.test(c.name))?.id || '') : '');
    if (suggested) setConsiderationStatusId(suggested);
  }, [returnTarget, returnTargets, returnFromStep, considerationStatuses]);

  const initEdits = useCallback((list) => {
    const cc = {}; const ap = {};
    list.forEach((po) => {
      cc[po.po_id] = {}; ap[po.po_id] = {};
      (po.items || []).forEach((it) => {
        cc[po.po_id][it.item_id] = {
          cc_qty: it.cost_control_quantity ?? '', cc_rate: it.cost_control_unit_rate ?? '',
          cc_amount: it.cost_control_amount ?? '', cc_gl: it.cost_control_general_ledger ?? '',
          cc_approved: !!it.cost_control_approved, cc_rejected: !!it.cost_control_rejected,
          cc_reason: it.cost_control_rejected_reason ?? '',
          boq_item_id: it.boq_item_id || '', boq_component_id: it.boq_component_id || '',
          // a line raised with only a BOQ code is matched to the project's BOQ once that list arrives
          boq_match: !it.boq_item_id && it.boq_item_code ? it.boq_item_code : null,
        };
        ap[po.po_id][it.item_id] = {
          qty: it.approved_quantity ?? '', rate: it.approved_unit_rate ?? '', amount: it.approved_amount ?? '',
          approved: !!it.approved, on_hold: !!it.on_hold, rejected: !!it.rejected, reason: it.rejected_reason ?? '',
        };
      });
    });
    setLineEdits(cc); setApprEdits(ap);
  }, []);

  // Each project's BOQ is fetched once; null while it is on its way.
  const boqRequested = useRef(new Set());
  const loadBoqs = useCallback((list) => {
    Array.from(new Set(list.map((po) => po.project_id).filter(Boolean))).forEach((pid) => {
      if (boqRequested.current.has(pid)) return;
      boqRequested.current.add(pid);
      setBoqByProject((p) => ({ ...p, [pid]: null }));
      api.listBOQ(pid)
        .then((res) => setBoqByProject((p) => ({ ...p, [pid]: Array.isArray(res) ? res : (res?.data || res?.items || []) })))
        .catch(() => setBoqByProject((p) => ({ ...p, [pid]: [] })));
    });
  }, []);

  // Once a project's BOQ list is in, give lines that only carried a BOQ code their BOQ item.
  useEffect(() => {
    setLineEdits((prev) => {
      let changed = false;
      const next = { ...prev };
      rows.forEach((po) => {
        const boqs = boqByProject[po.project_id];
        if (!boqs || !next[po.po_id]) return;
        Object.entries(next[po.po_id]).forEach(([itemId, e]) => {
          if (!e.boq_match) return;
          const hit = boqs.find((b) => (b.boq_code || '').toLowerCase() === e.boq_match.toLowerCase());
          next[po.po_id] = { ...next[po.po_id], [itemId]: { ...e, boq_match: null, boq_item_id: hit ? (hit.boq_item_id || hit.id) : '' } };
          changed = true;
        });
      });
      return changed ? next : prev;
    });
  }, [boqByProject, rows]);

  const refreshCounts = useCallback(() => {
    if (!isCC) return;
    api.clearApiCache();
    api.getMPOReviewCounts().then((c) => setCounts(c || {})).catch(() => {});
  }, [isCC]);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  const remember = (list) => setSeenOptions((prev) => {
    const projects = { ...prev.projects }; const suppliers = { ...prev.suppliers };
    list.forEach((r) => {
      if (r.project_id) projects[r.project_id] = `${r.project_code ? `${r.project_code} — ` : ''}${r.project_name || ''}`;
      if (r.supplier_id) suppliers[r.supplier_id] = r.supplier_name || '';
    });
    return { projects, suppliers };
  });

  const fetchRows = useCallback(async () => {
    if (isCC && !view) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      api.clearApiCache();
      let list;
      if (isPendingView) {
        const g = await api.getMPOPendingConsideration();
        setGroups(g || { total: 0, total_value: 0, groups: [] });
        const first = (g?.groups || [])[0];
        setOpenGroups((prev) => (Object.keys(prev).length ? prev : (first ? { [first.status_name]: true } : {})));
        list = (g?.groups || []).flatMap((x) => x.orders || []);
        setTotalCount(list.length);
      } else {
        const res = await api.getMPOReviewQueue(view, {
          limit: page.limit, offset: page.skip, search,
          project_id: filterProject, supplier_id: filterSupplier,
        });
        list = res?.items || [];
        setTotalCount(res?.total || 0);
      }
      setRows(list);
      remember(list);
      initEdits(list);
      if (ccEditing) loadBoqs(list);
    } catch (e) {
      toast.show(e.message || 'Failed to load purchase orders.', 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCC, view, isPendingView, ccEditing, page, search, filterProject, filterSupplier, initEdits, loadBoqs]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // ── line edit helpers ──────────────────────────────────────────────────────
  const clearFigureError = (poId, itemId, key) => setFigureErrors((prev) => {
    if (!prev[poId]?.[itemId]?.[key]) return prev;
    const lines = { ...prev[poId], [itemId]: { ...prev[poId][itemId] } };
    delete lines[itemId][key];
    if (!Object.keys(lines[itemId]).length) delete lines[itemId];
    return { ...prev, [poId]: lines };
  });

  const discOf = (poId, itemId) => {
    const po = rows.find((r) => r.po_id === poId);
    return po?.items?.find((i) => i.item_id === itemId)?.disc || 0;
  };

  const updateLine = (poId, itemId, field, value) => {
    if (!blank(value) && (field === 'cc_qty' || field === 'cc_rate')) clearFigureError(poId, itemId, field === 'cc_qty' ? 'qty' : 'rate');
    const disc = discOf(poId, itemId);
    setLineEdits((prev) => {
      const e = { ...(prev[poId]?.[itemId] || {}), [field]: value };
      if (field === 'cc_qty' || field === 'cc_rate') {
        const q = parseFloat(e.cc_qty); const r = parseFloat(e.cc_rate);
        e.cc_amount = !isNaN(q) && !isNaN(r) ? calcItemAmount(q, r, disc).toFixed(2) : '';
      }
      if (field === 'boq_item_id') e.boq_component_id = '';
      return { ...prev, [poId]: { ...(prev[poId] || {}), [itemId]: e } };
    });
  };

  const updateAppr = (poId, itemId, field, value) => {
    if (!blank(value) && (field === 'qty' || field === 'rate')) clearFigureError(poId, itemId, field);
    const disc = discOf(poId, itemId);
    setApprEdits((prev) => {
      const e = { ...(prev[poId]?.[itemId] || {}), [field]: value };
      if (field === 'qty' || field === 'rate') {
        const q = parseFloat(e.qty); const r = parseFloat(e.rate);
        e.amount = !isNaN(q) && !isNaN(r) ? calcItemAmount(q, r, disc).toFixed(2) : '';
      }
      return { ...prev, [poId]: { ...(prev[poId] || {}), [itemId]: e } };
    });
  };

  const setCCStatus = (poId, itemId, status) => {
    setLineEdits((prev) => ({
      ...prev,
      [poId]: { ...prev[poId], [itemId]: { ...prev[poId][itemId], cc_approved: status === 'approved', cc_rejected: status === 'rejected' } },
    }));
    if (status === 'rejected') setReasonModal({ poId, itemId, mode: 'cc', status });
  };

  const setApprStatus = (poId, itemId, status) => {
    setApprEdits((prev) => ({
      ...prev,
      [poId]: { ...prev[poId], [itemId]: { ...prev[poId][itemId], approved: status === 'approved', on_hold: status === 'on_hold', rejected: status === 'rejected' } },
    }));
    if (status === 'on_hold' || status === 'rejected') setReasonModal({ poId, itemId, mode: 'appr', status });
  };

  const glAppliesToAll = (poId) => glAllLines[poId] === true;
  const headerGl = (po) => ccHeader[po.po_id] ?? po.cost_control_general_ledger ?? '';
  const applyHeaderGl = (po, value) => setLineEdits((prev) => {
    const lines = { ...(prev[po.po_id] || {}) };
    (po.items || []).forEach((it) => { lines[it.item_id] = { ...(lines[it.item_id] || {}), cc_gl: value }; });
    return { ...prev, [po.po_id]: lines };
  });

  // Copy the ordered figures into Cost Control's columns (CC) or Cost Control's into Management's — a
  // starting point only: nothing is submitted, every line stays editable, no line is marked accepted.
  const copyOrderedToCC = (po) => {
    setLineEdits((prev) => {
      const lines = { ...(prev[po.po_id] || {}) };
      (po.items || []).forEach((it) => {
        lines[it.item_id] = { ...(lines[it.item_id] || {}), cc_qty: cleanFigure(it.quantity_ordered), cc_rate: cleanFigure(it.unit_rate), cc_amount: cleanFigure(it.amount) };
      });
      return { ...prev, [po.po_id]: lines };
    });
    setFigureErrors((prev) => ({ ...prev, [po.po_id]: {} }));
    toast.show(`Ordered figures copied into ${(po.items || []).length} line(s). Adjust any of them before you approve.`, 'success');
  };

  const copyCCToAppr = (po) => {
    const copyable = (po.items || []).filter((it) => it.cost_control_quantity != null || it.cost_control_unit_rate != null);
    if (!copyable.length) { toast.show('Cost Control has not entered any figures on this purchase order.', 'info'); return; }
    setApprEdits((prev) => {
      const lines = { ...(prev[po.po_id] || {}) };
      copyable.forEach((it) => {
        lines[it.item_id] = {
          ...(lines[it.item_id] || {}), qty: cleanFigure(it.cost_control_quantity), rate: cleanFigure(it.cost_control_unit_rate),
          amount: it.cost_control_amount != null ? cleanFigure(it.cost_control_amount) : '',
        };
      });
      return { ...prev, [po.po_id]: lines };
    });
    toast.show(`Cost Control figures copied into ${copyable.length} line(s). Adjust any of them before you approve.`, 'success');
  };

  const hasAnyReview = (po) => (po.items || []).some((it) => {
    const e = (isCC ? lineEdits : apprEdits)[po.po_id]?.[it.item_id] || {};
    return isCC ? (e.cc_approved || e.cc_rejected) : (e.approved || e.on_hold || e.rejected);
  });

  const buildCCItems = (po, autoFill) => (po.items || []).map((it) => {
    const e = lineEdits[po.po_id]?.[it.item_id] || {};
    return {
      item_id: it.item_id,
      cost_control_quantity: blank(e.cc_qty) ? null : parseFloat(e.cc_qty),
      cost_control_unit_rate: blank(e.cc_rate) ? null : parseFloat(e.cc_rate),
      cost_control_amount: blank(e.cc_amount) ? null : parseFloat(e.cc_amount),
      cost_control_general_ledger: (e.cc_gl ?? '').trim() || null,
      cost_control_approved: autoFill ? autoFill === 'approved' : !!e.cc_approved,
      cost_control_rejected: autoFill ? autoFill === 'rejected' : !!e.cc_rejected,
      cost_control_rejected_reason: autoFill === 'rejected' ? actionComment : (e.cc_rejected ? (e.cc_reason || null) : null),
      boq_item_id: e.boq_item_id || null,
      boq_component_id: e.boq_component_id || null,
    };
  });

  const buildApprItems = (po, autoFill) => (po.items || []).map((it) => {
    const e = apprEdits[po.po_id]?.[it.item_id] || {};
    return {
      item_id: it.item_id,
      approved_quantity: blank(e.qty) ? null : parseFloat(e.qty),
      approved_unit_rate: blank(e.rate) ? null : parseFloat(e.rate),
      approved_amount: blank(e.amount) ? null : parseFloat(e.amount),
      approved: autoFill ? autoFill === 'approved' : !!e.approved,
      on_hold: autoFill ? autoFill === 'on_hold' : !!e.on_hold,
      rejected: autoFill ? autoFill === 'rejected' : !!e.rejected,
      rejected_reason: (autoFill === 'rejected' || autoFill === 'on_hold') ? actionComment : ((e.rejected || e.on_hold) ? (e.reason || null) : null),
    };
  });

  // ── workflow actions ───────────────────────────────────────────────────────
  const openAction = async (po, type) => {
    // Every line that will carry money forward needs a quantity and a rate; rejected / held lines do not.
    if (type === 'approve') {
      const errs = {};
      (po.items || []).forEach((it) => {
        const e = (isCC ? lineEdits : apprEdits)[po.po_id]?.[it.item_id] || {};
        if (isCC ? e.cc_rejected : (e.rejected || e.on_hold)) return;
        const err = {};
        if (blank(isCC ? e.cc_qty : e.qty)) err.qty = true;
        if (blank(isCC ? e.cc_rate : e.rate)) err.rate = true;
        if (Object.keys(err).length) errs[it.item_id] = err;
      });
      setFigureErrors((prev) => ({ ...prev, [po.po_id]: errs }));
      if (Object.keys(errs).length) { toast.show('Enter a quantity and rate on every line you are approving.', 'error'); return; }
    }

    setActionComment(''); setNextStepInfo(null); setNextApproverId('');
    setReturnTargets([]); setReturnTarget(''); setReturnFromStep('');
    setConsiderationStatuses([]); setConsiderationStatusId('');
    setPreparing(`${po.po_id}:${type}`);
    try {
      if (type === 'approve') {
        const details = await api.getNextStepDetails(po.workflow_instance_id).catch(() => null);
        const data = details?.data || details;
        setNextStepInfo(data || null);
        if (data?.approver_mode === 'specific_user' && data?.eligible_users?.length) {
          setNextApproverId(data.eligible_users[0].user_id || data.eligible_users[0].id);
        }
      }
      if (type === 'return') {
        const [targets, statuses] = await Promise.all([
          api.getReturnTargets(po.workflow_instance_id).catch(() => null),
          api.listConsiderationStatuses({ activeOnly: true, returnReasonsOnly: true }).catch(() => null),
        ]);
        setReturnTargets(targets?.targets || []);
        setReturnFromStep(targets?.current_step_name || po.current_step_name || '');
        setReturnTarget(targets?.default_target || 'requester');
        setConsiderationStatuses(Array.isArray(statuses) ? statuses : (statuses?.consideration_statuses || []));
      }
    } finally {
      setPreparing(null);
    }
    setActionModal({ poId: po.po_id, instanceId: po.workflow_instance_id, type, reviewed: hasAnyReview(po) });
  };

  const submitAction = async () => {
    if (!actionModal) return;
    const { poId, instanceId, type } = actionModal;
    const meta = ACTION_META[type];
    if (meta.commentRequired && !actionComment.trim()) { toast.show(`Comment is required to ${meta.verb} this purchase order.`, 'error'); return; }
    if (type === 'return' && offersConsideration(returnFromStep, returnTargets, returnTarget) && !considerationStatusId) {
      toast.show('Choose a consideration status for this return.', 'error'); return;
    }
    const payload = { action: type, comment: actionComment };
    if (type === 'return' && returnTarget) payload.return_target = returnTarget;
    if (type === 'return' && considerationStatusId) payload.consideration_status_id = considerationStatusId;
    if (type === 'approve' && nextStepInfo?.approver_mode === 'specific_user') {
      if (!nextApproverId) { toast.show('Please select the specific approver.', 'error'); return; }
      payload.next_approver_id = nextApproverId;
    }
    const po = rows.find((r) => r.po_id === poId);
    setSubmitting(true);
    try {
      // The line review is saved with the action; with no line marked, every line follows the action.
      if (po && type !== 'resume') {
        const autoFill = hasAnyReview(po) ? null
          : ({ approve: 'approved', reject: 'rejected', hold: 'on_hold' }[type] || null);
        if (isCC) {
          await api.saveMPOCCReview(poId, { items: buildCCItems(po, autoFill), cost_control_general_ledger: headerGl(po) || null });
        } else {
          await api.saveMPOApprovalReview(poId, { items: buildApprItems(po, autoFill) });
        }
      }
      await api.actOnWorkflow(instanceId, payload);
      setActionModal(null);
      const ref = po?.po_number || po?.serial_number || '';
      const nextName = nextStepInfo && nextStepInfo.approver_mode !== 'is_final' ? nextStepInfo.step_name : null;
      setOutcome({
        type,
        title: { approve: 'Purchase Order Approved!', return: 'Purchase Order Returned!', reject: 'Purchase Order Rejected!', hold: 'Purchase Order Put On Hold!', resume: 'Hold Lifted!' }[type],
        message: {
          approve: nextName
            ? `Purchase Order ${ref} has been approved and forwarded to the next step — ${nextName}.`
            : `Purchase Order ${ref} has been approved. Its approval is now complete.`,
          return: `Purchase Order ${ref} has been returned${returnTarget && returnTarget !== 'requester'
            ? ` to ${(returnTargets.find((t) => t.value === returnTarget) || {}).label || 'the earlier step'}` : ' to the submitter for correction'}.`,
          reject: `Purchase Order ${ref} has been rejected. This is final — it cannot be resubmitted.`,
          hold: `Purchase Order ${ref} has been put on hold. It stays with you and you can act on it again later.`,
          resume: `Purchase Order ${ref} is active again and back in your queue for a decision.`,
        }[type],
      });
      if (type !== 'hold' && type !== 'resume') {
        setRows((prev) => prev.filter((r) => r.po_id !== poId));
        setGroups((prev) => {
          if (!prev?.groups) return prev;
          const g = prev.groups.map((x) => ({ ...x, orders: (x.orders || []).filter((o) => o.po_id !== poId) }))
            .map((x) => ({ ...x, count: x.orders.length })).filter((x) => x.count > 0);
          return { ...prev, groups: g, total: g.reduce((n, x) => n + x.count, 0) };
        });
      } else {
        fetchRows();
      }
      refreshCounts();
    } catch (e) {
      toast.show(e.message || `Failed to ${meta.verb} purchase order.`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (e, po) => {
    e.stopPropagation();
    setOpening(true);
    try { setViewing(await api.getMPO(po.po_id)); } catch (err) { toast.show(err.message || 'Failed to load purchase order.', 'error'); }
    finally { setOpening(false); }
  };

  const toggleRow = (poId) => {
    setExpanded((prev) => ({ ...prev, [poId]: !prev[poId] }));
    if (!expanded[poId]) api.markEntitySeen(poId, 'material_po').catch(() => {});
  };

  const sortedRows = useMemo(() => sortRows(rows, sortState, (row, key) => {
    if (key === 'grand_total' || key === 'cc_total') return parseFloat(key === 'cc_total' ? ccTotal(row) : row.grand_total) || 0;
    if (key === 'lines') return (row.items || []).length;
    return row[key] ?? '';
  }), [rows, sortState]);

  if (opening) return <RNLLoader title="Loading purchase order…" subtitle="Please wait" />;
  if (viewing) {
    return <MPODetailView po={viewing} readOnly onBack={() => setViewing(null)} onRefresh={() => api.getMPO(viewing.po_id).then(setViewing).catch(() => {})} onEdit={null} />;
  }

  const numInput = (val, onChange, hasError) => (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <input
        type="number" min="0" value={val}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 90, padding: '3px 6px', border: hasError ? '2px solid #ef4444' : '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, background: '#f8fafc', textAlign: 'right' }}
      />
      {hasError && (
        <div style={{ position: 'absolute', top: -28, left: 0, background: '#ef4444', color: '#fff', padding: '3px 7px', borderRadius: 4, fontSize: 10, fontWeight: 'bold', whiteSpace: 'nowrap', zIndex: 10, pointerEvents: 'none' }}>
          This field is required
        </div>
      )}
    </div>
  );

  // ── expanded panel: header, review summary, ordered lines, actions, review grid ──
  const renderExpanded = (po) => {
    const poId = po.po_id;
    const edits = lineEdits[poId] || {};
    const aEdits = apprEdits[poId] || {};
    const onHold = !!po.is_on_hold;
    const actionable = isMgmt || (ccEditing && isCostControlStep(po.current_step_name));
    const sendingBack = isAwaitingCorrection(po);
    const backTo = sendingBack ? correctionTarget(po) : '';
    const boqs = boqByProject[po.project_id] || [];
    const errs = figureErrors[poId] || {};
    const busy = (t) => preparing === `${poId}:${t}`;
    const disabledStyle = (blocked) => ({ cursor: preparing ? 'wait' : (blocked ? 'not-allowed' : 'pointer'), opacity: (preparing || blocked) ? 0.5 : 1, filter: blocked ? 'grayscale(0.35)' : 'none' });

    return (
      <div style={{ padding: '0 0 16px', background: '#f1f5f9', borderTop: '2px solid #3b82f6', borderBottom: '2px solid #cbd5e1' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 20px 8px', background: '#e8eef8', borderBottom: '1px solid #cbd5e1' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            PO: {po.po_number || po.serial_number} — {po.supplier_name || 'Unknown supplier'}
          </div>

          <div style={{ background: '#fff', border: '1px solid black', borderRadius: 8, padding: '12px 16px', marginTop: 4 }}>
            <div style={{ fontWeight: 800, color: '#1e3a8a', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
              Section 1 — Purchase Order Header
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', rowGap: 14, columnGap: 24 }}>
              <Field label="PO Number">{po.po_number || '—'}</Field>
              <Field label="Serial No.">{po.serial_number}</Field>
              <Field label="PO Date">{fmtDate(po.po_date)}</Field>
              <Field label="Project">{po.project_name}{po.project_code ? ` (${po.project_code})` : ''}</Field>
              <Field label="Supplier">{po.supplier_name}</Field>
              <Field label="Submitted By">{po.submitted_by_name}</Field>
              <Field label="Submitted Date">{fmtDate(po.submitted_date)}</Field>
              <Field label="Expected Delivery">{fmtDate(po.expected_delivery_date)}</Field>
              <Field label="Currency">{po.currency || 'NGN'}</Field>
              <Field label="Payment Terms">{po.payment_terms}</Field>
              <Field label="Delivery Terms">{po.delivery_terms}</Field>
              <Field label="Category">{po.category}</Field>
              <Field label="Grand Total"><span style={{ color: '#1e40af' }}>{money(po.grand_total)}</span></Field>
              {po.notes && <Field label="Notes" span={4}>{po.notes}</Field>}
            </div>
          </div>

          {isMgmt && (() => {
            const items = po.items || [];
            const accepted = items.filter((it) => it.cost_control_approved).length;
            const rejectedLines = items.filter((it) => it.cost_control_rejected).length;
            const subtotal = Number(po.subtotal_amount || 0);
            const ccApproved = items.filter((it) => !it.cost_control_rejected)
              .reduce((s, it) => s + Number(it.cost_control_amount ?? it.amount ?? 0), 0);
            const variance = subtotal - ccApproved;
            const within = variance >= 0;
            return (
              <div style={{ background: '#fff', border: '1px solid black', borderRadius: 8, padding: '12px 16px', marginTop: 4 }}>
                <div style={{ fontWeight: 800, color: '#2563eb', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
                  Section 2 — Cost Control Review Summary
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', rowGap: 14, columnGap: 24 }}>
                  <Field label="CC Officer">{po.cc_reviewed_by}</Field>
                  <Field label="CC Review Date">{po.cc_reviewed_at ? fmtDate(po.cc_reviewed_at) : '—'}</Field>
                  <Field label="CC Comment" span={2}>{po.cc_review_comment ? <i>"{po.cc_review_comment}"</i> : '—'}</Field>
                  <Field label="Accepted Lines">{accepted}</Field>
                  <Field label="Rejected Lines"><span style={{ color: rejectedLines ? '#ef4444' : undefined }}>{rejectedLines}</span></Field>
                  <Field label="PO Subtotal">{money(subtotal)}</Field>
                  <Field label="CC Approved"><span style={{ color: '#2563eb' }}>{money(ccApproved)}</span></Field>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: within ? '#16a34a' : '#ef4444' }}>● {within ? 'Within PO Value' : 'Above PO Value'}</span>
                  <span><span style={{ ...fieldLabel, marginRight: 6 }}>Variance</span>
                    <b style={{ fontSize: 12.5, color: within ? '#16a34a' : '#ef4444' }}>{money(Math.abs(variance))}</b></span>
                </div>
              </div>
            );
          })()}
        </div>

        <OrderedLines items={po.items || []} />

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '0 16px' }}>
          {actionable && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {isCC ? 'Cost Control Actions:' : 'Approval Actions:'}
              </div>
              <button onClick={() => openAction(po, 'approve')} disabled={!!preparing || onHold} style={{ ...pill('#16a34a'), ...disabledStyle(onHold) }}
                title={onHold ? 'This purchase order is on hold — resume it first.' : (sendingBack ? `Send the corrected purchase order back to ${backTo}` : 'Approve this purchase order')}>
                ✓ {busy('approve') ? 'Preparing…' : (sendingBack ? `Resubmit to ${backTo}` : 'Approve')}
              </button>
              <button onClick={() => openAction(po, 'return')} disabled={!!preparing || onHold} style={{ ...pill('#f97316'), ...disabledStyle(onHold) }}
                title={onHold ? 'This purchase order is on hold — resume it first.' : 'Return this purchase order'}>
                ↰ {busy('return') ? 'Preparing…' : 'Return'}
              </button>
              {isMgmt && (onHold ? (
                <button onClick={() => openAction(po, 'resume')} disabled={!!preparing} style={{ ...pill('#16a34a'), ...disabledStyle(false) }} title="Lift the hold">▶ Resume</button>
              ) : (
                <button onClick={() => openAction(po, 'hold')} disabled={!!preparing} style={{ ...pill('#eab308'), ...disabledStyle(false) }} title="Put this purchase order on hold">⏸ Hold</button>
              ))}
              <button onClick={() => openAction(po, 'reject')} disabled={!!preparing || onHold} style={{ ...pill('#ef4444'), ...disabledStyle(onHold) }}
                title={onHold ? 'This purchase order is on hold — resume it first.' : 'Reject this purchase order permanently'}>
                ✕ {busy('reject') ? 'Preparing…' : 'Reject'}
              </button>
            </>
          )}

          <div style={{ marginLeft: actionable ? 16 : 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#475569' }}>Cost Control GL:</span>
            <input
              type="text"
              value={actionable && isCC ? headerGl(po) : (po.cost_control_general_ledger || '')}
              disabled={!(actionable && isCC)}
              placeholder={actionable && isCC ? 'Enter GL code' : '—'}
              onChange={(e) => {
                const v = e.target.value;
                setCcHeader((prev) => ({ ...prev, [poId]: v }));
                if (glAppliesToAll(poId)) applyHeaderGl(po, v);
              }}
              style={{ padding: '5px 10px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 6, outline: 'none', width: 180, fontWeight: 600, color: '#0f172a',
                background: actionable && isCC ? '#fff' : '#f8fafc', cursor: actionable && isCC ? 'text' : 'not-allowed' }}
            />
            {actionable && isCC && (
              <label title="On: the header code fills the CC GL column for every line. Off: type a code on each line."
                style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={glAppliesToAll(poId)} style={{ cursor: 'pointer', width: 14, height: 14 }}
                  onChange={(ev) => { setGlAllLines((prev) => ({ ...prev, [poId]: ev.target.checked })); if (ev.target.checked) applyHeaderGl(po, headerGl(po)); }} />
                Same GL for every line
              </label>
            )}
          </div>

          {actionable && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
              <span style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>{isCC ? 'Cost Control values:' : 'Approved values:'}</span>
              <button type="button" onClick={() => (isCC ? copyOrderedToCC(po) : copyCCToAppr(po))}
                title={isCC ? 'Copy the ordered quantity, rate and amount into the Cost Control columns for every line.' : "Copy Cost Control's quantity, rate and amount into the approved columns for every line."}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#fff', color: '#1e3a8a', border: '1.5px solid #1e3a8a', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                ⧉ {isCC ? 'Copy ordered values' : 'Copy CC values'}
              </button>
            </div>
          )}
        </div>

        <div style={{ overflowX: 'auto', padding: '12px 0 0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, border: '1px solid #cbd5e1', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#0b318b', color: '#fff' }}>
                <th style={thS({ background: '#1e3a8a', width: 84 })}>ACTION</th>
                <th style={thS({ background: '#15317a', width: 130 })}>{actionable && isCC ? 'DECISION' : 'OUTCOME'}</th>
                <th style={thS({ minWidth: 50 })}>#</th>
                {isCC && (
                  <>
                    <th style={thS({ background: '#0e4429', minWidth: 160 })}>BOQ ITEM</th>
                    <th style={thS({ background: '#0e4429', minWidth: 160 })}>BOQ SUB-COMPONENT</th>
                  </>
                )}
                <th style={thS()}>ORD QTY</th>
                <th style={thS()}>RATE (₦)</th>
                <th style={thS()}>DISC %</th>
                <th style={thS()}>AMOUNT (₦)</th>
                <th style={thS()}>VAT (₦)</th>
                <th style={thS({ background: isCC ? '#1e3a8a' : '#0e7490', minWidth: 90 })}>CC QTY</th>
                <th style={thS({ background: isCC ? '#1e3a8a' : '#0e7490', minWidth: 90 })}>CC RATE (₦)</th>
                <th style={thS({ background: isCC ? '#1e3a8a' : '#0e7490', minWidth: 90 })}>CC AMT (₦)</th>
                <th style={thS({ background: isCC ? '#1e3a8a' : '#0e7490', minWidth: 110 })}>CC GL</th>
                {isMgmt && (
                  <>
                    <th style={thS({ background: '#0e7490', minWidth: 160 })}>BOQ</th>
                    <th style={thS({ background: '#0e7490', minWidth: 120 })}>CC DECISION</th>
                    <th style={thS({ background: '#1e3a8a', minWidth: 90 })}>APPR QTY</th>
                    <th style={thS({ background: '#1e3a8a', minWidth: 90 })}>APPR RATE (₦)</th>
                    <th style={thS({ background: '#1e3a8a', minWidth: 90 })}>APPR AMT (₦)</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {(po.items || []).map((it, idx) => {
                const e = edits[it.item_id] || {};
                const a = aEdits[it.item_id] || {};
                const rowBoq = boqs.find((b) => String(b.boq_item_id || b.id) === String(e.boq_item_id));
                const subs = rowBoq?.components || [];
                const glLocked = glAppliesToAll(poId);
                return (
                  <tr key={it.item_id} style={{ borderBottom: '1px solid #e2e8f0', background: idx % 2 ? '#f8fafc' : '#fff' }}>
                    <td style={tdS({ background: '#f0f6ff' })}>
                      {isCC && actionable ? (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <button title="Accept" onClick={() => setCCStatus(poId, it.item_id, e.cc_approved ? 'none' : 'approved')} style={markBtn(e.cc_approved, '#16a34a')}>✓</button>
                          <button title="Reject" onClick={() => setCCStatus(poId, it.item_id, e.cc_rejected ? 'none' : 'rejected')} style={markBtn(e.cc_rejected, '#ef4444')}>✕</button>
                        </div>
                      ) : isMgmt ? (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <button title="Approve" onClick={() => setApprStatus(poId, it.item_id, a.approved ? 'none' : 'approved')} style={markBtn(a.approved, '#16a34a')}>✓</button>
                          <button title="Hold" onClick={() => setApprStatus(poId, it.item_id, a.on_hold ? 'none' : 'on_hold')} style={markBtn(a.on_hold, '#eab308', '#a16207')}>⏸</button>
                          <button title="Reject" onClick={() => setApprStatus(poId, it.item_id, a.rejected ? 'none' : 'rejected')} style={markBtn(a.rejected, '#ef4444')}>✕</button>
                        </div>
                      ) : (
                        <div title={e.cc_approved ? 'Accepted' : e.cc_rejected ? 'Rejected' : 'No decision recorded'}
                          style={{ ...markBtn(e.cc_approved || e.cc_rejected, e.cc_approved ? '#16a34a' : e.cc_rejected ? '#ef4444' : '#cbd5e1', '#94a3b8'), margin: '0 auto', cursor: 'default' }}>
                          {e.cc_approved ? '✓' : e.cc_rejected ? '✕' : '—'}
                        </div>
                      )}
                      {isCC && e.cc_rejected && e.cc_reason && (
                        <div style={{ marginTop: 3, fontSize: 10, color: '#ef4444', fontStyle: 'italic', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{e.cc_reason}"</div>
                      )}
                      {isMgmt && (a.rejected || a.on_hold) && a.reason && (
                        <div style={{ marginTop: 3, fontSize: 10, color: a.rejected ? '#ef4444' : '#a16207', fontStyle: 'italic', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{a.reason}"</div>
                      )}
                    </td>
                    <td style={tdS({ background: '#e8f1ff', fontWeight: 700 })}>
                      {isCC
                        ? (e.cc_approved ? <span style={{ color: '#16a34a' }}>CC Approved</span> : e.cc_rejected ? <span style={{ color: '#ef4444' }}>CC Rejected</span> : <span style={{ color: '#94a3b8', fontWeight: 400 }}>—</span>)
                        : (a.approved ? <span style={{ color: '#16a34a' }}>Approved</span> : a.rejected ? <span style={{ color: '#ef4444' }}>Rejected</span> : a.on_hold ? <span style={{ color: '#a16207' }}>On Hold</span> : <span style={{ color: '#94a3b8', fontWeight: 400 }}>—</span>)}
                    </td>
                    <td style={tdS({ color: '#64748b' })}>{it.line_number || idx + 1}</td>
                    {isCC && (actionable ? (
                      <>
                        <td style={tdS({ background: '#f0fdf4' })}>
                          <SearchableSelect
                            value={e.boq_item_id || ''}
                            onChange={(v) => updateLine(poId, it.item_id, 'boq_item_id', v)}
                            style={boqByProject[po.project_id] === null ? ccSelectDisabledStyle : ccSelectStyle}
                            disabled={boqByProject[po.project_id] === null}
                            placeholder={boqByProject[po.project_id] === null ? 'Loading BOQ…' : 'Select BOQ'}
                            dropdownWidth={360}
                            options={boqs.map((b) => ({ value: b.boq_item_id || b.id, label: `${b.boq_code || ''} — ${b.description || ''}` }))}
                          />
                        </td>
                        <td style={tdS({ background: '#f0fdf4' })}>
                          <SearchableSelect
                            value={e.boq_component_id || ''}
                            onChange={(v) => updateLine(poId, it.item_id, 'boq_component_id', v)}
                            disabled={!e.boq_item_id || !subs.length}
                            style={(!e.boq_item_id || !subs.length) ? ccSelectDisabledStyle : ccSelectStyle}
                            placeholder="Select Sub-BOQ"
                            dropdownWidth={320}
                            options={subs.map((c) => ({ value: c.component_id || c.id, label: c.description || c.name }))}
                          />
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={tdS({ background: '#f0fdf4', textAlign: 'left' })}>{it.boq_item_description || it.boq_item_code || '—'}</td>
                        <td style={tdS({ background: '#f0fdf4', textAlign: 'left' })}>{it.boq_component_description || '—'}</td>
                      </>
                    ))}
                    <td style={tdS({ fontWeight: 600 })}>{num(it.quantity_ordered)}</td>
                    <td style={tdS()}>{money(it.unit_rate)}</td>
                    <td style={tdS()}>{it.disc != null ? `${it.disc}%` : '—'}</td>
                    <td style={tdS({ fontWeight: 700 })}>{money(it.amount)}</td>
                    <td style={tdS()}>{money(it.vat_amount)}</td>
                    {isCC && actionable ? (
                      <>
                        <td style={tdS({ background: '#f0f6ff' })}>{numInput(e.cc_qty ?? '', (v) => updateLine(poId, it.item_id, 'cc_qty', v), !!errs[it.item_id]?.qty)}</td>
                        <td style={tdS({ background: '#f0f6ff' })}>{numInput(e.cc_rate ?? '', (v) => updateLine(poId, it.item_id, 'cc_rate', v), !!errs[it.item_id]?.rate)}</td>
                        <td style={tdS({ background: '#f0f6ff', fontWeight: 600, color: '#1e40af' })}>{money(e.cc_amount)}</td>
                        <td style={tdS({ background: '#f0f6ff' })}>
                          <input
                            type="text" value={e.cc_gl ?? ''} disabled={glLocked}
                            onChange={(ev) => updateLine(poId, it.item_id, 'cc_gl', ev.target.value)}
                            placeholder={glLocked ? 'From header' : 'GL code'}
                            title={glLocked ? 'Filled from the header GL. Untick "Same GL for every line" to set this line on its own.' : 'Ledger code for this line'}
                            style={{ width: 96, padding: '4px 6px', fontSize: 11.5, textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: 5, outline: 'none',
                              background: glLocked ? '#f1f5f9' : '#fff', color: glLocked ? '#64748b' : 'inherit', cursor: glLocked ? 'not-allowed' : 'text' }}
                          />
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={tdS({ background: isCC ? '#f0f6ff' : '#f0fdfa' })}>{it.cost_control_quantity != null ? num(it.cost_control_quantity) : '—'}</td>
                        <td style={tdS({ background: isCC ? '#f0f6ff' : '#f0fdfa' })}>{money(it.cost_control_unit_rate)}</td>
                        <td style={tdS({ background: isCC ? '#f0f6ff' : '#f0fdfa', fontWeight: 600, color: '#0f766e' })}>{money(it.cost_control_amount)}</td>
                        <td style={tdS({ background: isCC ? '#f0f6ff' : '#f0fdfa' })}>{it.cost_control_general_ledger || po.cost_control_general_ledger || '—'}</td>
                      </>
                    )}
                    {isMgmt && (
                      <>
                        <td style={tdS({ background: '#f0fdfa', textAlign: 'left' })}>
                          {it.boq_item_description || it.boq_item_code || '—'}
                          {it.boq_component_description && <div style={{ fontSize: 10.5, color: '#64748b' }}>{it.boq_component_description}</div>}
                        </td>
                        <td style={tdS({ background: '#f0fdfa' })}>
                          {it.cost_control_approved && <span style={{ color: '#16a34a', fontWeight: 700 }}>✓ Accepted</span>}
                          {it.cost_control_rejected && <span style={{ color: '#ef4444', fontWeight: 700 }}>✕ Rejected</span>}
                          {!it.cost_control_approved && !it.cost_control_rejected && <span style={{ color: '#94a3b8' }}>—</span>}
                          {it.cost_control_rejected && it.cost_control_rejected_reason && (
                            <div style={{ marginTop: 3, fontSize: 10, color: '#ef4444', fontStyle: 'italic', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{it.cost_control_rejected_reason}"</div>
                          )}
                        </td>
                        <td style={tdS({ background: '#f0f6ff' })}>{numInput(a.qty ?? '', (v) => updateAppr(poId, it.item_id, 'qty', v), !!errs[it.item_id]?.qty)}</td>
                        <td style={tdS({ background: '#f0f6ff' })}>{numInput(a.rate ?? '', (v) => updateAppr(poId, it.item_id, 'rate', v), !!errs[it.item_id]?.rate)}</td>
                        <td style={tdS({ background: '#f0f6ff', fontWeight: 600, color: '#1e40af' })}>{money(a.amount)}</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {isCC && view === 'reviewed' && (
          <div style={{ padding: '6px 16px 0', fontSize: 11, color: '#16a34a', fontWeight: 700, fontStyle: 'italic' }}>✓ Already reviewed by Cost Control — view only.</div>
        )}
        {isCC && view === 'rejected' && (
          <div style={{ padding: '6px 16px 0', fontSize: 11, color: '#dc2626', fontWeight: 700, fontStyle: 'italic' }}>⛔ Rejected by Cost Control — view only.</div>
        )}
      </div>
    );
  };

  const statusBadge = (po) => <MPOStatusPill status={po.status} onHold={po.is_on_hold} />;

  const projectOptions = Object.entries(seenOptions.projects).map(([value, label]) => ({ value, label }));
  const supplierOptions = Object.entries(seenOptions.suppliers).map(([value, label]) => ({ value, label }));
  const colSpan = 11;
  const thB = { border: '1px solid black', textAlign: 'center' };
  const tdB = { padding: '11px 12px', border: '1px solid black', fontSize: 12.5, verticalAlign: 'middle', textAlign: 'center' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{'@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } } .mpo-rq th, .mpo-rq td { border: 1px solid black; }'}</style>

      {isMgmt && (
        <div>
          <h2 style={{ margin: '0 0 4px', fontWeight: 800, fontSize: 18, color: 'var(--text-primary)' }}>Management Approval</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
            Purchase orders Cost Control has reviewed. Approve, hold or reject each line, then act on the purchase order.
          </p>
        </div>
      )}

      {isCC && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {WORKSPACES.map((ws) => {
            const active = view === ws.key;
            return (
              <div
                key={ws.key}
                onClick={() => setView(ws.key)}
                onMouseEnter={(e) => { if (active) return; e.currentTarget.style.borderColor = ws.color; e.currentTarget.style.background = `${ws.color}14`; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                onMouseLeave={(e) => { if (active) return; e.currentTarget.style.borderColor = `${ws.color}33`; e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
                style={{
                  flex: '1 1 260px', minWidth: 240, cursor: 'pointer', borderRadius: 12, padding: 16, transition: 'all 0.15s',
                  border: `2px solid ${active ? ws.color : `${ws.color}33`}`,
                  background: active ? `${ws.color}14` : '#fff',
                  boxShadow: active ? '0 4px 12px rgba(0,0,0,0.08)' : 'var(--shadow-sm)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `${ws.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{ws.icon}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.5 }}>{ws.label}</div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: ws.color, borderRadius: 10, padding: '2px 9px', minWidth: 20, minHeight: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {counts[ws.key] != null ? counts[ws.key] : (
                      <span style={{ width: 10, height: 10, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: ws.color, margin: '10px 0 4px', textTransform: 'uppercase' }}>{ws.title}</div>
                <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{ws.desc}</div>
              </div>
            );
          })}
        </div>
      )}

      {isCC && !view ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)', border: '2px dashed #767b82ff', borderRadius: 12 }}>
          👆 Select a workspace above to load its purchase orders.
        </div>
      ) : (
        <>
          {!isPendingView && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '1 1 320px', maxWidth: 480 }}>
                <input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSearch(searchInput.trim()); }}
                  placeholder="Search PO number, project, supplier…"
                  style={{ flex: 1, minWidth: 0, height: 36, boxSizing: 'border-box', padding: '0 12px', fontSize: 13, border: '1.5px solid #cbd5e1', borderRadius: 8, outline: 'none', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1e293b)' }}
                />
                <button onClick={() => setSearch(searchInput.trim())} style={{ height: 36, padding: '0 20px', background: '#0b318b', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Search</button>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                <div style={{ width: 220 }}>
                  <Select title="Select Project" value={filterProject} placeholder="All projects" options={[{ value: '', label: 'All projects' }, ...projectOptions]} onChange={setFilterProject} />
                </div>
                <div style={{ width: 220 }}>
                  <Select title="Select Supplier" value={filterSupplier} placeholder="All suppliers" options={[{ value: '', label: 'All suppliers' }, ...supplierOptions]} onChange={setFilterSupplier} />
                </div>
              </div>
            </div>
          )}

          {loading ? <RNLLoader title="Loading purchase orders…" subtitle="Fetching the queue" /> : isPendingView ? (
            <Card style={{ border: '2.5px solid black', borderRadius: 0, boxShadow: 'none', overflow: 'hidden', padding: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '13px 18px', background: '#d97706', color: '#fff', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.06em' }}>⏳ PENDING CONSIDERATION — GROUPED BY STATUS</span>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{groups?.total || 0} records · {money(groups?.total_value || 0)}</span>
              </div>
              {!groups?.groups?.length ? (
                <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13.5, lineHeight: 1.7 }}>
                  Nothing is parked right now.
                  <div style={{ fontSize: 12, marginTop: 4 }}>Purchase orders appear here when Management sends one back to Cost Control.</div>
                </div>
              ) : groups.groups.map((g) => {
                const open = !!openGroups[g.status_name];
                return (
                  <div key={g.status_name} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <button
                      onClick={() => setOpenGroups((o) => ({ ...o, [g.status_name]: !o[g.status_name] }))}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', background: open ? '#fff7ed' : '#fff', border: 'none', borderLeft: `4px solid ${GROUP_COLOR[g.color] || '#94a3b8'}`, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                    >
                      <span style={{ fontSize: 16 }}>{g.icon || '•'}</span>
                      <strong style={{ fontSize: 13.5, color: 'var(--text-primary)' }}>{g.status_name}</strong>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{g.count} purchase order{g.count === 1 ? '' : 's'} · {money(g.total_value)}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
                    </button>
                    {open && (
                      <div style={{ overflowX: 'auto', borderTop: '1px solid #f1f5f9' }}>
                        <table className="mpo-rq" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              {['PO NUMBER', 'PROJECT', 'SUPPLIER', 'SUBMITTED BY', 'LINES', 'GRAND TOTAL', 'STATUS', 'CURRENTLY WITH', ''].map((h) => (
                                <th key={h} style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: '#475569' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {g.orders.map((r) => {
                              const atCC = isCostControlStep(r.current_step_name);
                              const rowOpen = atCC && !!expanded[r.po_id];
                              return (
                                <React.Fragment key={r.po_id}>
                                  <tr onClick={atCC ? () => toggleRow(r.po_id) : undefined} style={{ cursor: atCC ? 'pointer' : 'default', background: rowOpen ? '#eff6ff' : undefined }}>
                                    <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: '#0b318bff', fontFamily: 'monospace' }}>
                                      {atCC && <span style={{ marginRight: 6, fontSize: 10 }}>{rowOpen ? '▼' : '▶'}</span>}{r.po_number || r.serial_number}
                                    </td>
                                    <td style={{ padding: '9px 12px', textAlign: 'center', maxWidth: 180, whiteSpace: 'normal' }}>{r.project_name || '—'}</td>
                                    <td style={{ padding: '9px 12px', textAlign: 'center' }}>{r.supplier_name || '—'}</td>
                                    <td style={{ padding: '9px 12px', textAlign: 'center' }}>{r.submitted_by_name || '—'}</td>
                                    <td style={{ padding: '9px 12px', textAlign: 'center' }}>{(r.items || []).length}</td>
                                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700 }}>{money(r.grand_total)}</td>
                                    <td style={{ padding: '9px 12px', textAlign: 'center' }}>{statusBadge(r)}<StatusReason reason={r.return_reason} /></td>
                                    <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                      <span style={{ fontSize: 11.5, color: '#b45309', fontWeight: 700 }}>{r.currently_with || r.current_step_name || '—'}</span>
                                      {!atCC && <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>view only</div>}
                                    </td>
                                    <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                      <ActionIcon title={atCC ? 'Review' : 'View'} color="#0b318bff" onClick={(e) => openDetail(e, r)}><EyeIcon /></ActionIcon>
                                    </td>
                                  </tr>
                                  {rowOpen && (
                                    <tr><td colSpan={9} style={{ padding: 0, background: '#f8fafc' }}>{renderExpanded(r)}</td></tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 8 }}>{totalCount} purchase order{totalCount === 1 ? '' : 's'} found</div>
              <div ref={viewport.ref} style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, border: '2px solid black' }}>
                  <thead>
                    <tr style={{ background: '#0b318bff', color: '#fff' }}>
                      <SortTh label="PO NUMBER" sortKey="po_number" state={sortState} style={thB} />
                      <SortTh label="SUBMITTED DATE" sortKey="submitted_date" state={sortState} style={thB} />
                      <SortTh label="SUBMITTED BY" sortKey="submitted_by_name" state={sortState} style={thB} />
                      <SortTh label="PROJECT" sortKey="project_name" state={sortState} style={thB} />
                      <SortTh label="SUPPLIER" sortKey="supplier_name" state={sortState} style={thB} />
                      <SortTh label="LINES" sortKey="lines" state={sortState} style={thB} />
                      <SortTh label="GRAND TOTAL" sortKey="grand_total" state={sortState} style={thB} />
                      <SortTh label="CC AMOUNT" sortKey="cc_total" state={sortState} style={thB} />
                      <SortTh label="STATUS" sortKey="status" state={sortState} style={thB} />
                      <SortTh label="CURRENTLY WITH" sortKey="currently_with" state={sortState} style={thB} />
                      <SortTh label="ACTIONS" style={{ ...thB, width: 80 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {!sortedRows.length && (
                      <tr><td colSpan={colSpan} style={{ ...tdB, padding: '32px 20px', color: '#9ca3af' }}>
                        {search || filterProject || filterSupplier ? 'No matching purchase orders. Try clearing the filters.' : 'No purchase orders here.'}
                      </td></tr>
                    )}
                    {sortedRows.map((row, idx) => {
                      const isOpen = !!expanded[row.po_id];
                      return (
                        <React.Fragment key={row.po_id}>
                          <tr onClick={() => toggleRow(row.po_id)} style={{ background: isOpen ? '#dbeafe' : (idx % 2 ? '#f8fafc' : '#fff'), cursor: 'pointer' }}>
                            <td style={{ ...tdB, whiteSpace: 'nowrap' }}>
                              <span style={{ fontSize: 10, color: '#64748b', marginRight: 10 }}>{isOpen ? '▼' : '▶'}</span>
                              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0b318bff' }}>{row.po_number || '—'}</span><div style={{ fontSize: 10.5, fontWeight: 600, color: '#94a3b8', fontFamily: 'var(--font)' }}>Serial {row.serial_number}</div>
                            </td>
                            <td style={{ ...tdB, color: '#64748b' }}>{fmtDate(row.submitted_date)}</td>
                            <td style={tdB}>{row.submitted_by_name || '—'}</td>
                            <td style={tdB}>
                              <div style={{ fontWeight: 600 }}>{row.project_name || '—'}</div>
                              {row.project_code && <div style={{ fontSize: 11, color: '#94a3b8' }}>{row.project_code}</div>}
                            </td>
                            <td style={tdB}>{row.supplier_name || '—'}</td>
                            <td style={tdB}>{(row.items || []).length}</td>
                            <td style={{ ...tdB, fontFamily: 'monospace', fontWeight: 700, color: '#0b318bff', textAlign: 'right' }}>{money(row.grand_total)}</td>
                            <td style={{ ...tdB, fontFamily: 'monospace', fontWeight: 700, color: '#16a34a', textAlign: 'right' }}>{ccTotal(row) != null ? money(ccTotal(row)) : '—'}</td>
                            <td style={tdB}>
                              {statusBadge(row)}
                              <StatusReason reason={view === 'rejected' ? row.outcome_reason : (row.is_on_hold ? row.hold_comment : row.return_reason)} />
                            </td>
                            <td style={tdB}>{row.currently_with || '—'}</td>
                            <td style={tdB} onClick={(e) => e.stopPropagation()}>
                              <ActionIcon title="View purchase order" color="#0b318bff" onClick={(e) => openDetail(e, row)}><EyeIcon /></ActionIcon>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={colSpan} style={{ padding: 0, border: '1px solid black' }}>
                                <PinnedDetail width={viewport.width}>{renderExpanded(row)}</PinnedDetail>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ borderLeft: '2px solid black', borderRight: '2px solid black', borderBottom: '2px solid black' }}>
                <Pagination total={totalCount} skip={page.skip} limit={page.limit} onChange={setPage} />
              </div>
            </div>
          )}
        </>
      )}

      {reasonModal && (() => {
        const { poId, itemId, mode, status } = reasonModal;
        const isHold = status === 'on_hold';
        const value = mode === 'appr' ? (apprEdits[poId]?.[itemId]?.reason || '') : (lineEdits[poId]?.[itemId]?.cc_reason || '');
        const change = (v) => (mode === 'appr' ? updateAppr(poId, itemId, 'reason', v) : updateLine(poId, itemId, 'cc_reason', v));
        return (
          <Modal title={isHold ? 'Reason for Hold' : 'Rejection Reason'} onClose={() => setReasonModal(null)} width={400}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                {isHold ? 'Reason for putting this line on hold' : 'Reason for Rejection'} <span style={{ color: 'red' }}>*</span>
              </label>
              <textarea rows={3} value={value} onChange={(e) => change(e.target.value)} placeholder="Enter reason..."
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Btn variant="outline" onClick={() => setReasonModal(null)}>Cancel</Btn>
              <Btn style={{ background: isHold ? '#eab308' : '#ef4444', color: '#fff', border: 'none' }} onClick={() => setReasonModal(null)}>Save Reason</Btn>
            </div>
          </Modal>
        );
      })()}

      {actionModal && (() => {
        const po = rows.find((r) => r.po_id === actionModal.poId);
        const resubmit = actionModal.type === 'approve' && isAwaitingCorrection(po);
        const base = ACTION_META[actionModal.type];
        const meta = resubmit ? { ...base, label: 'Resubmit', title: 'Resubmit Purchase Order', verb: 'resubmit' } : base;
        const parking = actionModal.type === 'return' && offersConsideration(returnFromStep, returnTargets, returnTarget);
        return (
          <Modal title={meta.title} onClose={() => (submitting ? null : setActionModal(null))}>
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 13, marginBottom: 16 }}>
                You are about to <strong>{meta.verb}</strong> purchase order <strong>{po?.po_number || po?.serial_number}</strong>.
                {actionModal.type === 'reject' && ' This is final — it cannot be resubmitted.'}
                {actionModal.type === 'hold' && ' It stays with you and you can act on it again later.'}
              </p>
              {actionModal.reviewed === false && actionModal.type !== 'resume' && (
                <div style={{ display: 'flex', gap: 8, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                  <span>⚠️</span>
                  <span>
                    You did not review the line items.{' '}
                    {actionModal.type === 'approve' && `All line items will automatically be marked ${isCC ? 'ACCEPTED' : 'APPROVED'}.`}
                    {actionModal.type === 'reject' && "All line items will automatically be marked REJECTED, using your comment below as each line's reason."}
                    {actionModal.type === 'hold' && "All line items will automatically be marked ON HOLD, using your comment below as each line's reason."}
                    {actionModal.type === 'return' && 'You can still return this purchase order without reviewing them.'}
                  </span>
                </div>
              )}
              <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                Comment {meta.commentRequired && <span style={{ color: 'red' }}>*</span>}
              </label>
              <textarea rows={3} value={actionComment} onChange={(e) => setActionComment(e.target.value)} placeholder={meta.placeholder}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 16 }} />

              {actionModal.type === 'return' && returnTargets.length > 1 && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Return to <span style={{ color: 'red' }}>*</span></label>
                  <Select title="Return To" value={returnTarget} onChange={setReturnTarget}
                    options={returnTargets.map((t) => ({ value: t.value, label: t.is_default ? `${t.label} (default)` : t.label }))} />
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                    {returnTargets.find((t) => t.value === returnTarget)?.description || 'Choose where this purchase order goes back to.'}
                  </div>
                </div>
              )}
              {parking && considerationStatuses.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Consideration Status <span style={{ color: 'red' }}>*</span></label>
                  <Select title="Consideration Status" value={considerationStatusId} onChange={setConsiderationStatusId} placeholder="Choose a heading"
                    options={considerationOptionsFor(considerationStatuses, returnFromStep).map((c) => ({ value: c.id, label: `${c.icon ? `${c.icon} ` : ''}${c.name}` }))} />
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>Groups the purchase order under this heading in Pending Consideration.</div>
                </div>
              )}
              {actionModal.type === 'approve' && nextStepInfo && nextStepInfo.approver_mode !== 'is_final' && nextStepInfo.step_name && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                    {nextStepInfo.approver_mode === 'returning_step' ? `Goes back to - ${nextStepInfo.step_name}` : `Next Approval Step - ${nextStepInfo.step_name}`}
                  </label>
                  {nextStepInfo.approver_mode === 'returning_step' ? (
                    <ReturnDestinationNote stepName={nextStepInfo.step_name} />
                  ) : nextStepInfo.approver_mode === 'specific_user' ? (
                    <Select value={nextApproverId} onChange={setNextApproverId}
                      options={(nextStepInfo.eligible_users || []).map((u) => ({ value: u.user_id || u.id, label: `${u.name} (${u.email})` }))} />
                  ) : (
                    <Select value="step_queue" onChange={() => {}} options={[{ value: 'step_queue', label: `🏢 ${nextStepInfo.step_name}` }]} />
                  )}
                </div>
              )}
              {actionModal.type === 'approve' && nextStepInfo?.approver_mode === 'is_final' && (
                <div style={{ display: 'flex', gap: 8, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                  <span>⚠️</span><span>This is the final approval. The purchase order will be marked <strong>APPROVED</strong>.</span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Btn variant="outline" onClick={() => setActionModal(null)} disabled={submitting}>Cancel</Btn>
              <Btn onClick={submitAction} disabled={submitting || (parking && !considerationStatusId)} style={{ background: meta.color, color: '#fff', border: 'none' }}>
                {submitting ? 'Submitting...' : `Confirm ${meta.label}`}
              </Btn>
            </div>
          </Modal>
        );
      })()}

      {outcome && (() => {
        const st = OUTCOME_STYLE[outcome.type] || OUTCOME_STYLE.approve;
        const dismiss = () => { setOutcome(null); fetchRows(); refreshCounts(); };
        return (
          <Modal hideHeader width={420} onClose={dismiss}>
            <div style={{ padding: '24px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: st.bg, color: st.color, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, boxShadow: `0 0 0 8px ${st.ring}` }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">{st.icon}</svg>
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{outcome.title}</h3>
              <p style={{ margin: '0 0 24px', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>{outcome.message}</p>
              <button onClick={dismiss} style={{ background: st.gradient, color: '#fff', border: 'none', padding: '10px 32px', borderRadius: 20, fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: `0 4px 12px ${st.shadow}` }}>
                Great, Thanks!
              </button>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

// Cost Control's total for a PO (lines it did not reject), or null before it has priced any line.
function ccTotal(po) {
  const lines = (po.items || []).filter((it) => it.cost_control_amount != null && !it.cost_control_rejected);
  if (!lines.length) return null;
  return lines.reduce((s, it) => s + Number(it.cost_control_amount), 0);
}
